/**
 * Makes a page editor an excerpt source and destination (#277): registers it
 * with the in-tab store, wires the mark icon menu, the destination picker,
 * Ctrl+Shift+E, range adjustment, the "several excerpts would go" check, and
 * Remove-from-here when an embed is deleted. Returns the selection bar hooks
 * and the overlays to render.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Editor } from '@tiptap/core';
import { useExcerptStore, useExcerptStoreVersion, useRegisterExcerptEditor, type DocRef } from '../../contexts/ExcerptSyncContext';
import { useToast } from '../../contexts/ToastContext';
import { startActionGroup, endActionGroup } from '../../lib/actionTracking';
import { createExcerpt, isSourceType, NEXT_MEETING, type DestType, type ItemRef, type SendDest } from '../../lib/excerpts';
import { DestinationPicker, type PickedItem } from '../excerpts/DestinationPicker';
import { ExcerptMenu } from '../excerpts/ExcerptMenu';
import { nextMeetingToast, removeFrom } from '../excerpts/excerptActions';
import { NavigateBridge, useNavigateRef } from '../NavigateBridge';
import { ExcerptList } from '../excerpts/ExcerptList';
import { Scissors, ChevronDown, ChevronRight } from 'lucide-react';
import { findExcerptRange, type ExcerptKind } from './ExcerptMark';
import type { ExcerptBarHooks } from './SelectionBar';

type Range = { from: number; to: number };

export function useExcerptEditor(editor: Editor | null, host: DocRef | undefined): { bar?: ExcerptBarHooks; overlays: ReactNode; header: ReactNode } {
  const store = useExcerptStore();
  useExcerptStoreVersion(store);
  const [panelOpen, setPanelOpen] = useState(false);
  const toast = useToast();
  useRegisterExcerptEditor(host, editor);
  const [menu, setMenu] = useState<{ ids: string[]; anchor: HTMLElement } | null>(null);
  const [picking, setPicking] = useState<Range | null>(null);
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const hostKey = host ? `${host.type}:${host.id}` : '';
  // The send toast links to the meeting it reached; the editor may render outside a router (tests).
  const navRef = useNavigateRef();

  const openPicker = useCallback(() => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;
    if (!empty) setPicking({ from, to });
  }, [editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const embed = editor.storage.excerptEmbed;
    const mark = editor.storage.excerpt;
    if (!embed || !mark) return;
    embed.host = host ?? null;
    if (!host || !store) {
      embed.onRemove = null;
      mark.onIconClick = null;
      mark.confirmDrop = null;
      mark.onShortcut = null;
      return;
    }
    embed.onRemove = (ids) => {
      for (const id of ids) {
        const v = store.view(id);
        if (!v || !v.destinations.some((d) => d.type === host.type && d.id === host.id)) continue;
        removeFrom(store, v, host)
          .then(() => toast.success('Removed excerpt from this item'))
          .catch(() => toast.error("Couldn't remove the excerpt from this item"));
      }
    };
    mark.onIconClick = (ids, anchor) => setMenu({ ids, anchor });
    mark.confirmDrop = (n) => window.confirm(`This removes ${n} excerpts from every place they were sent. Continue?`);
    mark.onShortcut = openPicker;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, hostKey, store, openPicker, toast]);

  // Open in context: /page#excerpt-<id> scrolls to the passage.
  useEffect(() => {
    if (!editor || !host) return;
    const m = /^#excerpt-([A-Za-z0-9-]{1,128})$/.exec(window.location.hash);
    if (!m) return;
    const t = setTimeout(() => {
      if (editor.isDestroyed) return;
      const range = findExcerptRange(editor.state.doc, m[1]);
      if (!range) return;
      editor.chain().setTextSelection(range).run();
      const { node } = editor.view.domAtPos(range.from);
      (node instanceof Element ? node : node.parentElement)?.scrollIntoView({ block: 'center' });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, hostKey]);

  /** Marks the range and records it live at once; the source autosave makes it active on the server. */
  const markRange = useCallback((range: Range, id: string, kind: ExcerptKind) => {
    if (!editor) return;
    editor.chain().setTextSelection(range).setExcerpt({ id, kind }).run();
    editor.storage.excerpt.live.set(id, kind);
    editor.commands.refreshExcerpts();
  }, [editor]);

  const send = useCallback(async (range: Range, picked: PickedItem[]) => {
    if (!editor || !host || !store || picked.length === 0) return;
    const text = editor.state.doc.textBetween(range.from, range.to, ' ');
    const open = picked.filter((p) => p.type !== NEXT_MEETING && isSourceType(p.type) && store.editorFor(p));
    const placed: ItemRef[] = open.map((p) => ({ type: p.type as DestType, id: p.id }));
    // #360: a next meeting the picker expects to reach, open in this tab, places its own embed too.
    for (const p of picked) {
      if (p.type === NEXT_MEETING && p.meetingId && store.editorFor({ type: 'meeting_instance', id: p.meetingId })) {
        placed.push({ type: 'meeting_instance', id: p.meetingId });
      }
    }
    const destinations: SendDest[] = picked.map((p) => (p.type === NEXT_MEETING
      ? { type: NEXT_MEETING, id: p.id, ...(p.date ? { date: p.date } : {}) }
      : { type: p.type, id: p.id }));
    // One undo step: the create, this document's save, and each open destination's save.
    startActionGroup(1 + placed.length);
    try {
      const out = await createExcerpt({
        source_type: host.type, source_id: host.id, kind: 'excerpt', text, destinations, placed,
      });
      markRange(range, out.id, 'excerpt');
      for (const p of open) store.editorFor(p)?.commands.insertExcerptEmbed(out.id);
      const reached = out.next_meetings ?? [];
      const placedKeys = new Set(placed.map((p) => `${p.type}:${p.id}`));
      for (const m of reached) {
        if (placedKeys.has(`meeting_instance:${m.id}`)) store.editorFor({ type: 'meeting_instance', id: m.id })?.commands.insertExcerptEmbed(out.id);
      }
      store.invalidate([out.id]);
      if (reached.length > 0) {
        nextMeetingToast(toast, reached, navRef.current, picked.length - reached.length);
      } else {
        toast.success(picked.length === 1 ? `Excerpt sent to ${picked[0].title}` : `Excerpt sent to ${picked.length} items`);
      }
    } catch {
      endActionGroup();
      toast.error("Couldn't create the excerpt");
    }
  }, [editor, host, store, markRange, toast]);

  const createItem = useCallback(async (kind: 'task' | 'note', title: string, range: Range) => {
    if (!editor || !host) throw new Error('Not an excerpt source');
    const text = editor.state.doc.textBetween(range.from, range.to, ' ');
    startActionGroup();
    try {
      const out = await createExcerpt({ source_type: host.type, source_id: host.id, kind, title, text });
      markRange(range, out.id, kind);
      store?.invalidate([out.id]);
      toast.success(kind === 'task' ? 'Task created' : 'Note created');
    } catch (err) {
      endActionGroup();
      toast.error(`Couldn't create the ${kind}`);
      throw err;
    }
  }, [editor, host, store, markRange, toast]);

  const bar: ExcerptBarHooks | undefined = useMemo(() => (host && store ? {
    onExcerpt: (range) => setPicking(range),
    onCreateItem: createItem,
    adjusting: !!adjusting,
    onApplyRange: () => {
      if (!editor || !adjusting) return;
      const { from, to, empty } = editor.state.selection;
      if (empty) return;
      if (editor.commands.adjustExcerptRange(adjusting, from, to)) toast.success('Excerpt range updated');
      setAdjusting(null);
    },
    onCancelAdjust: () => setAdjusting(null),
  } : undefined), [host, store, createItem, adjusting, editor, toast]);

  const overlays = (
    <>
      <NavigateBridge navRef={navRef} />
      {picking && host && (
        <DestinationPicker
          exclude={host}
          onClose={() => { setPicking(null); editor?.commands.focus(); }}
          onConfirm={(picked) => { const r = picking; setPicking(null); void send(r, picked); }}
        />
      )}
      {menu && host && (
        <ExcerptMenu
          ids={menu.ids}
          anchor={menu.anchor}
          where="source"
          host={host}
          onAdjust={(id) => {
            const range = editor ? findExcerptRange(editor.state.doc, id) : null;
            if (!editor || !range) return;
            editor.chain().focus().setTextSelection(range).run();
            setAdjusting(id);
            toast.info('Select the new range, then choose "Set excerpt range" in the bar');
          }}
          onClose={() => setMenu(null)}
        />
      )}
    </>
  );

  const jump = useCallback((id: string) => {
    const range = editor ? findExcerptRange(editor.state.doc, id) : null;
    if (!editor || !range) return;
    editor.chain().focus().setTextSelection(range).run();
    const { node } = editor.view.domAtPos(range.from);
    (node instanceof Element ? node : node.parentElement)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [editor]);

  // "Excerpts (n)" above the document: every excerpt in it, managed in place.
  const count = host && store ? store.sourceCount(host) : 0;
  const header = host && store && count > 0 ? (
    <div className="border-b border-border bg-bg-secondary/40">
      <button
        type="button"
        aria-expanded={panelOpen}
        onClick={() => setPanelOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 px-3 py-1 text-xs text-text-secondary hover:text-text-primary"
      >
        {panelOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Scissors size={12} className="text-excerpt" /> Excerpts ({count})
      </button>
      {panelOpen && (
        <div className="max-h-80 overflow-y-auto px-3 pb-3">
          <ExcerptList key={count} mode="source" type={host.type} id={host.id} onJump={jump} onChanged={() => { void store.refreshSource(host); }} />
        </div>
      )}
    </div>
  ) : null;

  return { bar, overlays, header };
}
